import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Loader2, Phone, Mail, MapPin, Clock, Headphones } from "lucide-react";
import { company } from "@/data/company";
import { contacts, TEAM_IMG } from "@/data/contacts";
import { Avatar } from "@/components/Avatar";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Vyplňte prosím všechna pole.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Zpráva byla odeslána. Brzy se vám ozveme!");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Zprávu se nepodařilo odeslat. Zkuste to prosím později.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-gavito-light/60 border border-gavito-navy/15 rounded-xl px-4 py-3 text-gavito-navy font-medium placeholder:text-gavito-navy/35 focus:outline-none focus:border-gavito-green focus:ring-2 focus:ring-gavito-green/20 transition";

  return (
    <div data-testid="contact-page" className="bg-gavito-light">
      <Seo title="Kontakt" description="Ozvěte se nám – kontakty, kterým se dá dovolat. Telefon, e-mail a sídlo GAVITO technic s.r.o. v Brně." path="/kontakt" />
      {/* HERO */}
      <section className="pt-40 md:pt-48 pb-14">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-4xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-gavito-green mb-6">Kontakt</p>
            <h1 className="font-heading font-black text-5xl md:text-7xl tracking-tighter leading-[0.95] text-gavito-navy">
              Kontakty, kterým se dá <span className="text-gavito-green">dovolat.</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* BACK OFFICE TEAM */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.15em] bg-gavito-green/12 text-gavito-green mb-5">
              <Headphones className="w-4 h-4" /> Back office tým
            </span>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl tracking-tight text-gavito-navy">
              Lidé, které <span className="text-gavito-green">slyšíte v telefonu</span>
            </h2>
            <p className="text-lg text-gavito-navy/70 max-w-2xl mx-auto leading-relaxed mt-4">
              Než se k vám lektor dostane, projde celá organizace přes náš back office. Tým, který drží
              termíny, smlouvy i komunikaci pohromadě.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {contacts.map((c) => (
              <div key={c.id} data-testid={`contact-person-${c.id}`} className="bg-white border border-gavito-navy/10 rounded-3xl p-5 flex flex-col hover:shadow-[0_20px_50px_-30px_rgba(27,42,78,0.35)] transition-shadow">
                <Avatar src={c.img ? TEAM_IMG + c.img : null} name={c.name} initials={c.initials} className="mb-4" />
                <h3 className="font-heading font-bold text-lg text-gavito-navy text-center mb-2">{c.name}</h3>
                <p className="text-sm text-gavito-navy/70 leading-relaxed text-center mb-4 flex-grow">„{c.quote}"</p>
                <div className="pt-3 border-t border-gavito-navy/10 space-y-2">
                  <a href={`mailto:${c.email}`} data-testid={`contact-email-${c.id}`} className="flex items-center gap-2 text-[12px] text-gavito-green hover:text-gavito-navy font-medium break-all transition-colors">
                    <Mail className="w-3.5 h-3.5 shrink-0" /> <span>{c.email}</span>
                  </a>
                  <a href={`tel:${c.phone.replace(/\s/g, "")}`} data-testid={`contact-phone-${c.id}`} className="flex items-center gap-2 text-[12px] text-gavito-green hover:text-gavito-navy font-semibold transition-colors">
                    <Phone className="w-3.5 h-3.5 shrink-0" /> <span>{c.phone}</span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-8">
          {/* INFO */}
          <div className="lg:col-span-5">
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl tracking-tight text-gavito-navy mb-8">Kde nás najdete</h2>
            <div className="bg-white border border-gavito-navy/10 rounded-3xl p-8 space-y-6">
              {[
                { icon: MapPin, label: "Adresa", value: company.address },
                { icon: Phone, label: "Telefon", value: company.phone, href: `tel:${company.phone}` },
                { icon: Mail, label: "E-mail", value: company.email, href: `mailto:${company.email}` },
                { icon: Clock, label: "Otevírací doba", value: company.hours },
              ].map((c) => (
                <div key={c.label} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-gavito-green/12 flex items-center justify-center shrink-0">
                    <c.icon className="w-5 h-5 text-gavito-green" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-gavito-navy/40">{c.label}</p>
                    {c.href ? (
                      <a href={c.href} className="text-lg font-semibold text-gavito-navy hover:text-gavito-green transition-colors">{c.value}</a>
                    ) : (
                      <p className="text-lg font-semibold text-gavito-navy">{c.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 bg-gavito-navy text-white rounded-3xl p-6">
              <p className="font-heading font-bold text-lg">{company.name}</p>
              <p className="text-white/70 text-sm mt-1">IČO: {company.ico} · DIČ: {company.dic}</p>
              <p className="text-white/50 text-xs mt-3 leading-relaxed">{company.rejstrik}</p>
            </div>
          </div>

          {/* FORM */}
          <div className="lg:col-span-7">
            <form onSubmit={handleSubmit} data-testid="contact-form" className="bg-white border border-gavito-navy/10 rounded-[2rem] p-8 md:p-12">
              <h2 className="font-heading font-extrabold text-3xl tracking-tight text-gavito-navy mb-8">Napište nám</h2>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gavito-navy/70 mb-2">Jméno a příjmení</label>
                  <input data-testid="contact-name" type="text" value={form.name} onChange={update("name")} className={inputCls} placeholder="Jan Novák" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gavito-navy/70 mb-2">E-mail</label>
                  <input data-testid="contact-email" type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="jan@email.cz" />
                </div>
              </div>
              <div className="mt-6">
                <label className="block text-sm font-semibold text-gavito-navy/70 mb-2">Předmět</label>
                <input data-testid="contact-subject" type="text" value={form.subject} onChange={update("subject")} className={inputCls} placeholder="Zájem o kurz AI" />
              </div>
              <div className="mt-6">
                <label className="block text-sm font-semibold text-gavito-navy/70 mb-2">Zpráva</label>
                <textarea data-testid="contact-message" rows={5} value={form.message} onChange={update("message")} className={`${inputCls} resize-none`} placeholder="Dobrý den, mám zájem o…" />
              </div>
              <button data-testid="contact-submit" type="submit" disabled={loading}
                className="mt-8 inline-flex items-center justify-center gap-2 bg-gavito-navy text-white font-semibold px-8 py-4 rounded-full hover:bg-gavito-green transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Odesílám…</> : <><Send className="w-4 h-4" /> Odeslat zprávu</>}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
